// src/services/UsageTrackingService.ts
import { getLogger } from "../helpers/logger";
import { emit } from "../messaging";

const logger = getLogger("UsageTracking");

export interface UsageStats {
  totalImports: number;
  lastImportDate: string | null;
  sessionCount: number;
  lastActiveDate: string;
  featureUsage: {
    urlImports: number;
    codeImports: number;
    fileImports: number;
  };
  upgradeClicks: number;
  tabSwitches: {
    url: number;
    code: number;
    file: number;
    account: number;
  };
}

/**
 * A FRESH default object per call, not a shared constant.
 *
 * `stats` was initialised to a module-level `DEFAULT_STATS` and every tracker mutates in place
 * (`this.stats.totalImports++`), so the "defaults" drifted with usage: anything later resetting
 * to them restored the counts already accumulated rather than zeros. `lastActiveDate` was worse —
 * frozen at whatever `new Date()` returned when the module first loaded.
 */
function defaultStats(): UsageStats {
  return {
  totalImports: 0,
  lastImportDate: null,
  sessionCount: 0,
  lastActiveDate: new Date().toISOString(),
  featureUsage: {
    urlImports: 0,
    codeImports: 0,
    fileImports: 0,
  },
  upgradeClicks: 0,
    tabSwitches: {
      url: 0,
      code: 0,
      file: 0,
      account: 0,
    },
  };
}

class UsageTrackingService {
  private stats: UsageStats = defaultStats();
  private lastSaveTime = 0;
  private saveDebounceMs = 1000; // Save at most once per second

  // Stats are restored by App.tsx, which hands them to `setStats` when the sandbox answers
  // LOAD_DATA with a PLUGIN_DATA message. There is no load path here: a `loadStats()` that only
  // logged "Usage tracking service initialized" used to sit in this spot, describing that
  // mechanism as if it implemented it.

  /**
   * Save stats to storage (debounced)
   */
  private async saveStats(): Promise<void> {
    const now = Date.now();
    if (now - this.lastSaveTime < this.saveDebounceMs) {
      return; // Skip save if called too frequently
    }

    try {
      this.lastSaveTime = now;
      // `emit`, not a hand-built `parent.postMessage`.
      //
      // This posted `{ pluginMessage: { type: "SAVE_USAGE_STATS", stats } }`. The wire format is
      // `{ name, args }`, and the sandbox dispatcher returns early on anything without a string
      // `name` — so the message was DROPPED, every time, and the line below logged that stats had
      // been saved. Measured against the real sandbox handler: the `type:` shape leaves storage
      // empty; the `{name,args}` envelope persists. So nothing was ever written, and the value
      // App.tsx restores on load had never been anything but absent.
      emit("SAVE_USAGE_STATS", this.stats);
      logger.info("Usage stats saved");
    } catch (error) {
      logger.error("Failed to save usage stats:", error);
    }
  }

  /**
   * Set initial stats (called when loading from storage)
   */
  public setStats(stats: Partial<UsageStats>): void {
    this.stats = { ...defaultStats(), ...stats };
  }

  /**
   * Get current stats
   */
  /** A deep copy: a shallow spread still handed out the live `featureUsage` and `tabSwitches`. */
  public getStats(): UsageStats {
    return {
      ...this.stats,
      featureUsage: { ...this.stats.featureUsage },
      tabSwitches: { ...this.stats.tabSwitches },
    };
  }

  /**
   * Track an import
   */
  public trackImport(type: "url" | "code" | "file"): void {
    this.stats.totalImports++;
    this.stats.lastImportDate = new Date().toISOString();

    if (type === "url") {
      this.stats.featureUsage.urlImports++;
    } else if (type === "code") {
      this.stats.featureUsage.codeImports++;
    } else if (type === "file") {
      this.stats.featureUsage.fileImports++;
    }

    this.saveStats();
    logger.info(`Import tracked: ${type}`);
  }

  /**
   * Track a tab switch
   */
  public trackTabSwitch(tab: "url" | "code" | "file" | "account"): void {
    this.stats.tabSwitches[tab]++;
    this.saveStats();
  }

  /**
   * Track upgrade button click
   */
  public trackUpgradeClick(): void {
    this.stats.upgradeClicks++;
    this.saveStats();
    logger.info("Upgrade click tracked");
  }

  /**
   * Increment session count
   */
  public trackSession(): void {
    this.stats.sessionCount++;
    this.stats.lastActiveDate = new Date().toISOString();
    this.saveStats();
    logger.info(`Session tracked: ${this.stats.sessionCount}`);
  }

  /**
   * Get usage summary for display
   */
  public getSummary(): {
    totalImports: number;
    favoriteFeature: string;
    daysSinceLastImport: number | null;
  } {
    const { urlImports, codeImports, fileImports } = this.stats.featureUsage;

    // Determine favorite feature
    let favoriteFeature = "URL Import";
    let maxUsage = urlImports;

    if (codeImports > maxUsage) {
      favoriteFeature = "Code Import";
      maxUsage = codeImports;
    }
    if (fileImports > maxUsage) {
      favoriteFeature = "File Import";
    }

    // Calculate days since last import
    let daysSinceLastImport: number | null = null;
    if (this.stats.lastImportDate) {
      const lastImport = new Date(this.stats.lastImportDate);
      const now = new Date();
      const diffMs = now.getTime() - lastImport.getTime();
      daysSinceLastImport = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }

    return {
      totalImports: this.stats.totalImports,
      favoriteFeature,
      daysSinceLastImport,
    };
  }

  /**
   * Reset all stats (for testing or user request)
   */
  public resetStats(): void {
    // `{ ...DEFAULT_STATS }` was a SHALLOW copy of a shared object whose nested counters the
    // trackers had been incrementing in place, so a reset left `featureUsage` and `tabSwitches`
    // exactly as they were — the thing a reset is for.
    this.stats = defaultStats();
    this.saveStats();
    logger.info("Usage stats reset");
  }
}

// Export singleton instance
export const usageTrackingService = new UsageTrackingService();
